import React, { useCallback, useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Searchable single-select with a "Load more" pager for lists that are already
 * in memory (pre-filtered / availability-sorted / shift-scoped). For lists that
 * come straight from `/participants` or `/staff`, use AsyncSearchSelect instead
 * so search and paging run on the server.
 *
 * options: [{ value, label, keywords?, disabled?, title?, render?, testid?, className? }]
 *   - `keywords` is extra text the search matches (NDIS number, email, …)
 *   - `render` replaces the label inside the row (badges, conflict reasons)
 * clearLabel/clearValue: optional first row such as "All participants" or
 *   "None (Open Shift)"; picking it calls onChange(clearValue).
 * triggerProps: extra attributes for the trigger button (id, aria-*).
 */
export default function SearchSelect({
  options = [],
  value,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No results',
  pageSize = 20,
  disabled = false,
  clearLabel,
  clearValue = '',
  selectedLabel,
  className,
  triggerProps,
  testid,
  testId,
}) {
  const tid = testId || testid;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [visible, setVisible] = useState(pageSize);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => `${o.label || ''} ${o.keywords || ''}`.toLowerCase().includes(q));
  }, [options, query]);

  const shown = filtered.slice(0, visible);
  const isClear = clearLabel && (value === undefined || value === null || String(value) === String(clearValue));
  const selected = options.find(o => String(o.value) === String(value));
  const currentLabel = selected ? selected.label : (isClear ? clearLabel : (value ? selectedLabel : ''));

  const close = () => { setOpen(false); setQuery(''); setVisible(pageSize); };

  // Radix Dialog locks page scroll and swallows wheel events on portalled
  // popovers, so drive the list manually while a lock is active.
  const handleWheel = useCallback((e) => {
    if (!document.body.hasAttribute('data-scroll-locked')) return;
    e.currentTarget.scrollTop += e.deltaY;
  }, []);

  return (
    <Popover open={open} onOpenChange={(o) => { if (o) setOpen(true); else close(); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between font-normal', className)}
          data-testid={tid}
          {...triggerProps}
        >
          <span className={cn('truncate', !currentLabel && 'text-muted-foreground')}>
            {currentLabel || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[220px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={(v) => { setQuery(v); setVisible(pageSize); }}
            data-testid={tid ? `${tid}-search` : undefined}
          />
          <CommandList onWheel={handleWheel} className="max-h-72 overflow-y-auto overscroll-contain" data-testid={tid ? `${tid}-list` : undefined}>
            {filtered.length === 0 && <CommandEmpty>{emptyText}</CommandEmpty>}
            <CommandGroup>
              {clearLabel && !query.trim() && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => { onChange(clearValue); close(); }}
                  data-testid={tid ? `${tid}-clear` : undefined}
                >
                  <Check className={cn('mr-2 h-4 w-4', isClear ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate text-muted-foreground">{clearLabel}</span>
                </CommandItem>
              )}
              {shown.map((o) => (
                <CommandItem
                  key={o.value}
                  value={String(o.value)}
                  disabled={o.disabled}
                  title={o.title}
                  className={o.className}
                  onSelect={() => { if (o.disabled) return; onChange(o.value); close(); }}
                  data-testid={o.testid || (tid ? `${tid}-option-${o.value}` : undefined)}
                >
                  <Check className={cn('mr-2 h-4 w-4 shrink-0', String(value) === String(o.value) ? 'opacity-100' : 'opacity-0')} />
                  {o.render ? o.render : <span className="truncate">{o.label}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
            {filtered.length > visible && (
              <div className="p-1 border-t">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs"
                  onClick={() => setVisible(v => v + pageSize)}
                  data-testid={tid ? `${tid}-load-more` : undefined}
                >
                  Load more ({filtered.length - visible} more)
                </Button>
              </div>
            )}
            {filtered.length > 0 && (
              <div className="px-3 py-2 text-[11px] text-muted-foreground border-t" data-testid={tid ? `${tid}-count` : undefined}>
                Showing {shown.length} of {filtered.length}
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
